import type {
  GetPaymentAssignmentDetailResponseDto,
  CarrierApplication as CarrierApplicationDto,
} from '../../../api/dto/get-payment-assignment-detail.dto'
import {
  PaymentCase,
  PaymentHistoryEntry,
  Priority,
  PaymentStatus,
  PaymentType,
  NOT_AVAILABLE,
  roleToActor,
} from '../Utils/types'
import type { GetPaymentAssignmentBenefitEventsResponseDto } from '../../../api/dto/get-payment-assignment-benefit-events.dto'

export const BENEFIT_EVENT_ACTION_TYPE_MAP: Record<
  string,
  PaymentHistoryEntry['actionType']
> = {
  'pa.update_status': 'Status Updated',
  'pa.payment_info_update': 'Payment Info Updated',
}

/** Try in order: new key, then legacy keys for backward compat */
const CARRIER_APP_PAYMENT_KEYS: readonly (readonly string[])[] = [
  ['PrimaryPaymentMethod', 'primaryPaymentMethod'],
  ['PrimaryACHAccountNumber'],
  ['PrimaryACHAccountType'],
  ['PrimaryACHAccountUse'],
]

const CARRIER_APP_PAYMENT_FIELD_NAMES = [
  'carrierApplicationPrimaryPaymentMethod',
  'carrierApplicationPrimaryACHAccountNumber',
  'carrierApplicationPrimaryACHAccountType',
  'carrierApplicationPrimaryACHAccountUse',
] as const

export function getCarrierApplicationPaymentFields(
  carrierApp: CarrierApplicationDto | null | undefined
): {
  carrierApplicationPrimaryPaymentMethod: string | undefined
  carrierApplicationPrimaryACHAccountNumber: string | undefined
  carrierApplicationPrimaryACHAccountType: string | undefined
  carrierApplicationPrimaryACHAccountUse: string | undefined
} {
  const raw = carrierApp as Record<string, unknown> | null | undefined
  if (!raw) {
    return {
      carrierApplicationPrimaryPaymentMethod: undefined,
      carrierApplicationPrimaryACHAccountNumber: undefined,
      carrierApplicationPrimaryACHAccountType: undefined,
      carrierApplicationPrimaryACHAccountUse: undefined,
    }
  }
  const out = {} as Record<string, string | undefined>
  CARRIER_APP_PAYMENT_KEYS.forEach((keys, i) => {
    const val = keys.reduce<string | undefined>(
      (acc, k) => acc ?? (raw[k] as string | undefined),
      undefined
    )
    out[CARRIER_APP_PAYMENT_FIELD_NAMES[i]] =
      val != null && val !== '' ? String(val) : undefined
  })
  return out as {
    carrierApplicationPrimaryPaymentMethod: string | undefined
    carrierApplicationPrimaryACHAccountNumber: string | undefined
    carrierApplicationPrimaryACHAccountType: string | undefined
    carrierApplicationPrimaryACHAccountUse: string | undefined
  }
}

/** Map API detail response to PaymentCase-like shape for PaymentDetailModal */
function pickBenefitVirtualCardFields(
  benefit: GetPaymentAssignmentDetailResponseDto['benefit'] | null | undefined
): {
  virtualCardId?: string
  virtualCardType?: string
  virtualCardKey?: string
} {
  if (!benefit) return {}
  const raw = benefit as unknown as Record<string, unknown>
  const idVal = raw.VirtualCardId ?? raw.virtualCardId
  const typeVal = raw.VirtualCardType ?? raw.virtualCardType
  const keyVal = raw.VirtualCardKey ?? raw.virtualCardKey
  return {
    virtualCardId:
      typeof idVal === 'string' && idVal.trim() !== '' ? idVal : undefined,
    virtualCardType:
      typeof typeVal === 'string' && typeVal.trim() !== ''
        ? typeVal
        : undefined,
    virtualCardKey:
      typeof keyVal === 'string' && keyVal.trim() !== '' ? keyVal : undefined,
  }
}

export function mapDetailToPaymentCase(
  detail: GetPaymentAssignmentDetailResponseDto,
  id: number
): PaymentCase & Record<string, unknown> {
  const person = detail.benefit?.person
  const carrierApp = detail.benefit?.carrierApplication
  const { virtualCardId, virtualCardType, virtualCardKey } =
    pickBenefitVirtualCardFields(detail.benefit)
  const carrierAppPaymentFields = getCarrierApplicationPaymentFields(carrierApp)
  // EPS resolves the method from the realtime benefit first; the carrier
  // application JSON often lacks it, which left the section showing N/A.
  const primaryPaymentMethod =
    detail.primaryPaymentMethod?.trim() ||
    carrierAppPaymentFields.carrierApplicationPrimaryPaymentMethod

  return {
    id,
    benefitId: detail.benefitId,
    month: detail.month,
    year: detail.year,
    priority: detail.priority as Priority,
    expectedCost: detail.expectedCost ?? null,
    actualCost: detail.actualCost ?? null,
    groupId: detail.groupId ?? null,
    paymentType: (detail.paymentType as PaymentType) ?? null,
    paymentStatus: {
      id: (detail.paymentStatus as PaymentStatus) ?? null,
      description: detail.paymentStatus ?? null,
    },
    assigneeId: detail.assigneeId ?? null,
    escalatorId: detail.escalatorId ?? null,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
    benefit: detail.benefit
      ? {
          // insured person & employee publickey
          id: detail.benefit.id,
          employeeName: detail.benefit.employeeName,
          employeePublicKey: detail.benefit.employeePublicKey,
          companyName: detail.benefit.companyName,
          carrierName: detail.benefit.carrierName ?? null,
          benefitEffectiveDate: detail.benefit.benefitEffectiveDate ?? null,
          benefitEndDate: detail.benefit.benefitEndDate ?? null,
          person: detail.benefit.person
            ? { id: detail.benefit.person.id }
            : undefined,
          carrierApplication:
            (detail.benefit.carrierApplication as unknown as Record<
              string,
              unknown
            >) ?? undefined,
        }
      : null,
    // Flattened fields for PaymentDetailModal
    companyEmployeeId: person?.companyEmployeeId ?? NOT_AVAILABLE,
    firstName: person?.firstName ?? '',
    middleName: person?.middleName ?? '',
    lastName: person?.lastName ?? '',
    relationship: person?.relationship ?? 'Employee',
    dateOfBirth: person?.dateOfBirth ?? '',
    zipCode: (person?.mailingPostalCode || person?.postalCode) ?? '',
    memberId: detail.benefit?.planPolicyNumber ?? NOT_AVAILABLE,
    ssn: person?.ssn ?? '',
    last4SSN: person?.ssnlastFour ?? '',
    applicationId: carrierApp?.ApplicationId ?? NOT_AVAILABLE,
    companyName: detail.benefit?.companyName ?? '',
    carrierName: detail.benefit?.carrierName ?? '',
    carrier: detail.benefit?.carrierName ?? '',
    binderMethod: carrierApp?.BinderPaymentType ?? NOT_AVAILABLE,
    mailingStreetAddress: person?.mailingStreetAddress ?? '',
    mailingCity: person?.mailingCity ?? '',
    mailingStateProvince: person?.mailingStateProvince ?? '',
    mailingCounty: person?.mailingCounty ?? '',
    mailingPostalCode: person?.mailingPostalCode ?? '',
    hixmeEmailAlias: person?.hixmeEmailAlias ?? '',
    employmentStatus: person?.employmentStatus ?? '',
    paymentMethods: [],
    source: '',
    activityLogs: [],
    billingId: carrierApp?.BillingId ?? NOT_AVAILABLE,
    planName: detail.benefit?.planName ?? NOT_AVAILABLE,
    ...carrierAppPaymentFields,
    carrierApplicationPrimaryPaymentMethod: primaryPaymentMethod,
    needsBinderPayment: detail.needsBinderPayment ?? null,
    isBinderPayment: detail.isBinderPayment ?? null,
    migrateToId: detail.migrateToId ?? null,
    virtualCardId,
    virtualCardType,
    virtualCardKey,
    healthPlanId: detail.healthPlanId,
  }
}

/** Build activity log entries from benefit events */
export function mapBenefitEventsToActivityLog(
  events: GetPaymentAssignmentBenefitEventsResponseDto
): PaymentHistoryEntry[] {
  return events.map((event, idx) => {
    const hasCreatedByUser = event.createdByUser != null
    const actorName = hasCreatedByUser
      ? (event.createdByUser?.name ?? undefined)
      : event.createdBy
    const statusAfter = event.data?.status ?? ''
    const note = event.data?.note?.trim()
    const performedByRole = hasCreatedByUser
      ? event.createdByUser?.role
      : actorName

    return {
      id: String(event.id ?? `${event.createdAt}-${idx}`),
      timestamp: event.createdAt,
      actionType: BENEFIT_EVENT_ACTION_TYPE_MAP[event.type ?? ''] ?? 'Unknown',
      actor: roleToActor(performedByRole),
      hasCreatedByUser,
      actorName,
      performedByRole,
      assigneeName: event.data?.assigneeToUser?.name,
      escalatorName: event.data?.escalatorToUser?.name,
      performedByEmail: event.createdByUser?.email,
      assigneeEmail: event.data?.assigneeToUser?.email,
      escalatorEmail: event.data?.escalatorToUser?.email,
      statusAfter,
      notes: note,
      priority: event.data?.metadata?.priority as number | undefined,
      metadata: event.data?.metadata as Record<string, unknown> | undefined,
      snapshot: event.data?.paymentInfo as
        | Record<string, string | boolean | null | undefined>
        | undefined,
    }
  })
}
